import { supabase } from '../lib/supabase';

const workflowTemplateService = {
  /**
   * Get all workflow templates (user's own + system templates)
   */
  async getAllTemplates() {
    try {
      const { data, error } = await supabase?.from('workflow_templates')?.select('*')?.order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching workflow templates:', error);
      throw new Error(error?.message || 'Failed to fetch workflow templates');
    }
  },

  /**
   * Get templates by category
   */
  async getTemplatesByCategory(category) {
    try {
      const { data, error } = await supabase?.from('workflow_templates')?.select('*')?.eq('category', category)?.order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching templates by category:', error);
      throw new Error(error?.message || 'Failed to fetch templates');
    }
  },

  /**
   * Get a single template by ID
   */
  async getTemplateById(id) {
    try {
      const { data, error } = await supabase?.from('workflow_templates')?.select('*')?.eq('id', id)?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error fetching template:', error);
      throw new Error(error?.message || 'Failed to fetch template');
    }
  },

  /**
   * Create a new workflow template
   */
  async createTemplate(templateData) {
    try {
      const { data, error } = await supabase?.from('workflow_templates')?.insert([{
          name: templateData?.name,
          description: templateData?.description || '',
          nodes: templateData?.nodes || [],
          connections: templateData?.connections || [],
          user_id: null, // No auth system implemented yet
          is_system: false, // Explicitly mark as user template
          category: templateData?.category || 'custom',
          tags: templateData?.tags || [],
          thumbnail_data: templateData?.thumbnailData || null
        }])?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating workflow template:', error);
      throw new Error(error?.message || 'Failed to create workflow template');
    }
  },

  /**
   * Update an existing workflow template
   */
  async updateTemplate(id, templateData) {
    try {
      const { data, error } = await supabase?.from('workflow_templates')?.update({
          name: templateData?.name,
          description: templateData?.description,
          nodes: templateData?.nodes,
          connections: templateData?.connections,
          category: templateData?.category,
          tags: templateData?.tags,
          thumbnail_data: templateData?.thumbnailData
        })?.eq('id', id)?.select();

      if (error) {
        console.error('Supabase update error:', error);
        throw error;
      }
      
      // Check if update actually happened
      if (!data || data?.length === 0) {
        throw new Error('Template update failed. Please try again.');
      }
      
      return data?.[0] || data;
    } catch (error) {
      console.error('Error updating workflow template:', error);
      throw new Error(error?.message || 'Failed to update workflow template');
    }
  },

  /**
   * Delete a workflow template
   */
  async deleteTemplate(id) {
    try {
      const { error } = await supabase?.from('workflow_templates')?.delete()?.eq('id', id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting workflow template:', error);
      throw new Error(error?.message || 'Failed to delete workflow template');
    }
  },

  /**
   * Search templates by name or tags
   */
  async searchTemplates(query) {
    try {
      const { data, error } = await supabase?.from('workflow_templates')?.select('*')?.or(`name.ilike.%${query}%,description.ilike.%${query}%`)?.order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error searching templates:', error);
      throw new Error(error?.message || 'Failed to search templates');
    }
  },

  /**
   * Delete templates by name (exact match)
   */
  async deleteTemplatesByName(names) {
    try {
      const { data, error } = await supabase?.from('workflow_templates')?.delete()?.in('name', names)?.select();

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error deleting templates by name:', error);
      throw new Error(error?.message || 'Failed to delete templates');
    }
  }
};

export default workflowTemplateService;